import React from 'react';
import ReactDOM from 'react-dom';
import {Amplify, Auth} from 'aws-amplify';
import {BrowserRouter as Router} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import config from './config';
import reportWebVitals from './reportWebVitals';
import TripPlanner from './containers/TripPlanner';
import Timetables from './containers/Timetables';
import axios from 'axios';
import log from 'loglevel';
import {ConfigProvider} from 'antd';
import StopTimetablesList from './containers/StopTimetablesList';
import ShiftBats from './features/ShiftBat/ShiftBats';
import {routeModelData, routesModelExpiryService, stopModelData} from './services/ModelService';
import ExpressBooking from './features/Bookings/ExpressBooking';
import store from "store2";
import { setUserOperator } from './services/routeService';

import './dayjs';
import en_GB from 'antd/locale/en_GB';

const restoreRegex = /^[/]restore$/;
const refreshRegex = /^[/]refresh$/;
const logoutRegex = /^[/]logout$/;
const switchRegex = /^[/]switch[/]([a-zA-Z0-9_-]+)[/]([a-zA-Z0-9_-]+)$/;
 

if (!config.local) {
    console.log(`Configuring AMPLIFY...`);
    console.log('COGNITO: ', config.cognito);
    Amplify.configure({
        Auth: {
            mandatorySignIn: true,
            region: config.cognito.REGION,
            userPoolId: config.cognito.USER_POOL_ID,
            identityPoolId: config.cognito.IDENTITY_POOL_ID,
            userPoolWebClientId: config.cognito.APP_CLIENT_ID
        },
        Storage: {
            region: config.s3.REGION,
            bucket: config.s3.BUCKET,
            identityPoolId: config.cognito.IDENTITY_POOL_ID
        },
        API: config.apiGateway.API
    });

    axios.defaults.timeout = 300000;
    axios.interceptors.request.use((config) => {
        config.timeout = 300000;
        return config;
    }, (error) => {
        return Promise.reject(error);
    });
    
    // Suppress console errors for 503/429 responses (these are handled with retry logic)
    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const statusCode = error?.response?.status;
            // Mark 503/429 errors as handled to prevent console noise
            // These errors are automatically retried with exponential backoff
            if (statusCode === 503 || statusCode === 429) {
                error._isHandled = true; // Mark as handled
            }
            return Promise.reject(error);
        }
    );
}

export const getFullUrl = () => {
    const protocol = window.location.protocol;
    const domain = window.location.hostname;
    const port = window.location.port;
    return `${protocol}//${domain}${port && port.length ? `:${port}` : ''}`;
};

const loadSite = () => {

    log.setDefaultLevel('WARN');
    // log.getLogger("TripPlanner").setLevel("DEBUG");
    // log.getLogger("BusRoute").setLevel("DEBUG");
    // log.getLogger("Schedule").setLevel("DEBUG");
    log.getLogger("ModelService").setLevel("DEBUG");
    // log.getLogger("PointManager").setLevel("DEBUG")
    if (process.env.REACT_APP_DEBUG_TRACKING) {
        log.getLogger('Tracking').setLevel('DEBUG');
    }

    // Initialize Sentry for prod and not localhost
    if (process.env.REACT_APP_STAGE === 'prod' || !window.location.hostname.includes('localhost')) {
        Sentry.init({
            dsn: 'https://f505d6076d6b7caee84d7888dd1d9ab6@o4508322276900864.ingest.de.sentry.io/4509092534747216',
            environment: process.env.REACT_APP_STAGE,
            beforeSend(event, hint) {
                // Filter out 503/429 errors - these are expected and handled with retry logic
                const error = hint.originalException;
                if (error?.response?.status === 503 || error?.response?.status === 429) {
                    return null; // Don't send to Sentry
                }
                // Check if error message contains 503/429
                if (error?.message?.includes('503') || error?.message?.includes('429')) {
                    return null;
                }
                return event;
            },
            ignoreErrors: [
                // Ignore 503 Service Unavailable errors (handled with retry logic)
                'Request failed with status code 503',
                'Request failed with status code 429',
                'Service Unavailable',
                '503',
                '429'
            ]
        });
    }


    let src = document.currentScript.src;
    if (!src.includes('?')) {
        src = window.location.search;
    }
    const params = new URLSearchParams('?' + src.split('?')[1]);
    const operatorId = params.get('operator_id');
    const relPath = params.get('rel_path');
    const [view, type] = params.get('view')?.split(':') || [];
    const views = params.get('views')?.split(',');
    const containers = params.get('containers')?.split(',');


    if (operatorId?.length && view?.length) {
        console.log('Openning view...');
        ReactDOM.render(
            <React.StrictMode>
                <Router>
                    <ConfigProvider theme={{
                        token: {
                            fontFamily: 'Work Sans',
                        },
                        hashed: false,
                        locale: en_GB,
                    }}>
                        <div className={`App publicContainer py-3 trip-planner-wrap`}>
                            {view === 'timetables' ?
                                <Timetables operatorId={operatorId}/> :
                                view === 'trip_planner' ?
                                    <TripPlanner operatorId={operatorId}/> :
                                    view === 'school_list' ?
                                        <StopTimetablesList operatorId={operatorId} relPath={relPath} view={type !== 'card'}/> :
                                        view === 'venue_list' ?
                                            <StopTimetablesList operatorId={operatorId} stopType={'venue'} relPath={relPath}
                                                                view={type !== 'card'}/> :
                                            view === 'shiftbats' ?
                                                <ShiftBats operatorId={operatorId}/> :
                                                view === 'bookings' ?
                                                    <ExpressBooking operatorId={operatorId} isEmbedded={true}/> :
                                                    <>Incorrect setup. Please check your configuration setting.</>
                            }
                        </div>
                    </ConfigProvider>
                </Router>
            </React.StrictMode>,
            document.getElementById('busable-embed') || document.getElementById('root')
        );
    } else if (operatorId?.length && views?.length && containers?.length) {
        console.log('Openning views...');
        views.forEach((screen, idx) => {
            let [view, type] = screen?.split(':') || [];
            ReactDOM.render(
                <React.StrictMode>
                    <Router>
                        <ConfigProvider theme={{
                            token: {
                                fontFamily: 'Work Sans',
                            },
                            hashed: false,
                            locale: en_GB,
                        }}>
                            <div className={`App publicContainer py-3 trip-planner-wrap`}>
                                {view === 'timetables' ?
                                    <Timetables operatorId={operatorId}/> :
                                    view === 'trip_planner' ?
                                        <TripPlanner operatorId={operatorId}/> :
                                        view === 'school_list' ?
                                            <StopTimetablesList operatorId={operatorId} view={type !== 'card'}/> :
                                            view === 'venue_list' ?
                                                <StopTimetablesList operatorId={operatorId} stopType={'venue'}
                                                                    view={type !== 'card'}/> :
                                                view === 'shiftbats' ?
                                                    <ShiftBats operatorId={operatorId}/> :
                                                    <>Incorrect setup. Please check your configuration setting.</>
                                }
                            </div>
                        </ConfigProvider>
                    </Router>
                </React.StrictMode>,
                document.getElementById(containers[idx])
            );
        });
    } else {
        console.log('Openning main app...');
        ReactDOM.render(
            <React.StrictMode>
                <Router>
                    <ConfigProvider theme={{
                        token: {
                            fontFamily: 'Work Sans',
                        },
                        hashed: false,
                        locale: en_GB,
                    }}>
                        <App/>
                    </ConfigProvider>
                </Router>
            </React.StrictMode>,
            document.getElementById('root')
        );
    }
}

if (restoreRegex.test(window.location.pathname)) {
    try {
        store.clearAll();
    } catch(e) {
        console.error('Error removing cache: ', e);
    }
    (async () => {
        await routesModelExpiryService.deleteAllDbs()
    })().catch((e) => {
        console.log(e);
    }).finally(() => {
        setTimeout(() => {
            window.location.replace('/login')
        }, 1000);
    });
    
} else if (refreshRegex.test(window.location.pathname)) {
    (async () => {
        await routesModelExpiryService.deleteAllDbs()
    })().catch((e) => {
        console.log(e);
    }).finally(() => {
        setTimeout(() => {
            window.location.replace('/')
        }, 1000);
    });
    
} else if (logoutRegex.test(window.location.pathname)) {
    Auth.signOut().then(() => {
        routesModelExpiryService.deinit();
        setTimeout(() => window.location.replace('/login'), 1000)
    }).then(() => {
        console.log('Logged out.');
    }).catch ((e) => {
        console.error('Error logging out; ', e);
    })
} else if (switchRegex.test(window.location.pathname)) {
    const [, companyId, operatorId] = window.location.pathname.match(switchRegex);
    console.log('Setting operator: ', operatorId)
    try {
        store.session.set("busable-operator-id", operatorId)
        setUserOperator({companyId, operatorId}).then(() => {
            window.location.replace('/')
        })
    } catch (e) {
        console.log(e);
    }
} else {
    loadSite();

    // If you want to start measuring performance in your app, pass a function
    // to log results (for example: reportWebVitals(console.log))
    // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
    reportWebVitals();
}
